/**
 * The per-system screens (REQ-021, "The screens").
 *
 * One component for all seven groups rather than seven pages, because the
 * screens differ only in a word and a filter, and because a group added to the
 * metric registry should acquire a screen without anybody remembering to build
 * one. That is the same rule the requirement states about figures: a metric
 * that reaches no screen is invisible, and a figure nobody looks at is a figure
 * nobody maintains.
 *
 * The address carries a `system/` segment rather than the group alone. The
 * sibling screens — executive, cohorts, definitions — are static paths under
 * the same prefix, and a bare `/admin/dashboard/:group` would sit one new
 * screen away from swallowing them. Static routes win that match today; the
 * segment means nobody has to know that.
 *
 * These screens show every metric in their group, the ten that reach the
 * executive screen included. The executive screen is a selection from the same
 * readings, not a different measurement, and a figure that appeared on one and
 * not the other would invite the question of which is the real one.
 */
import { Layers, ServerCog } from "lucide-react";
import { Link, NavLink, useParams } from "react-router-dom";
import MetricScreen from "./MetricScreen";
import {
  groupLabel,
  groupOrder,
  groupPath,
  isMetricGroup,
  METRIC_GROUPS,
} from "./metrics";

/** The seven screens, reachable from each other without going via the menu. */
function SystemTabs({ current }: { current: string }) {
  return (
    <nav
      data-testid="system-tabs"
      aria-label="Metric systems"
      className="flex flex-wrap gap-1 rounded-lg border border-gray-200 bg-white p-1"
    >
      {METRIC_GROUPS.map((group) => (
        <NavLink
          key={group}
          to={groupPath(group)}
          aria-current={group === current ? "page" : undefined}
          className={`rounded px-3 py-1.5 text-sm font-medium ${
            group === current
              ? "bg-sky-50 text-sky-800"
              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
          }`}
        >
          {groupLabel(group)}
        </NavLink>
      ))}
    </nav>
  );
}

export default function SystemDashboardPage() {
  const { group = "" } = useParams<{ group: string }>();

  // An address naming a system that does not exist is a broken link, not an
  // empty company. Saying which systems there are is the whole of the fix.
  if (!isMetricGroup(group)) {
    return (
      <div className="space-y-6 p-6">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <ServerCog className="h-5 w-5 text-sky-600" /> No such system
        </h1>
        <p
          data-testid="unknown-system"
          className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          Nothing the dashboard measures is grouped under &ldquo;{group}&rdquo;.
          No figure is missing; this address names a system that does not exist.
        </p>
        <SystemTabs current="" />
      </div>
    );
  }

  return (
    <MetricScreen
      title={groupLabel(group)}
      icon={ServerCog}
      select={(readings) => groupOrder(readings, group)}
      emptyMessage={`The dashboard was read and no metric is grouped under ${groupLabel(
        group,
      )}. That is a gap in the metric registry rather than a system with nothing to say about itself.`}
      intro={
        <>
          Every metric in this group, in the order the registry declares them —
          not only the ones that reach the{" "}
          <Link
            to="/admin/dashboard/executive"
            className="font-medium text-sky-700 hover:text-sky-900"
          >
            executive screen
          </Link>
          . Each figure carries its own definition and the records composing it.
        </>
      }
    >
      <SystemTabs current={group} />

      {/* AC-004 lives on its own screen and is linked from the group whose
          question it answers, rather than duplicated into a second table that
          would have to be corrected twice. */}
      {group === "customers" && (
        <p
          data-testid="cohorts-link"
          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600"
        >
          <Layers
            className="h-4 w-4 shrink-0 text-sky-600"
            aria-hidden="true"
          />
          <span>
            The figures below are aggregates, and an aggregate hides whether
            newer months behave better than older ones —{" "}
            <Link
              to="/admin/dashboard/cohorts"
              className="font-medium text-sky-700 hover:text-sky-900"
            >
              the same measures by signup cohort
            </Link>
            .
          </span>
        </p>
      )}
    </MetricScreen>
  );
}
